/**
 * The qualified reference's folder half (I-1774, `docs/concept-file-grants.md` §12.9.2), in a module
 * of its own: the file page's address reads it in the app's launch entry, and the path finder in
 * `repository-paths.ts` (4.8 KB) has no business there (T-0559).
 */

/**
 * I-1774 (`docs/concept-file-grants.md` §12.9.2): a reference that names its work-area folder and
 * the device the folder is on, then the path: `featit-app@macbook-pro:tasks/x.md`. The folder is a
 * work-area folder's name (one segment, no `@`); the device is that folder's device handle
 * ({@link deviceHandle}). A plain path is not one and keeps its own rules (§12.2).
 */
export interface IFileReferenceFolder {
  /** The work-area folder's name, `featit-app`. */
  name: string;
  /** The folder's device handle, `macbook-pro`. */
  device: string;
}

export const FOLDER_NAME = '[A-Za-z0-9._+-]+';
/** A device handle: lower case letters and digits, `-` between them, at most 63 characters. */
export const DEVICE_HANDLE = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?';
const DEVICE_HANDLE_PATTERN = new RegExp(`^${DEVICE_HANDLE}$`);

/** Whether `value` is a device handle as {@link deviceHandle} makes them. */
export function isDeviceHandle(value: string): boolean {
  return DEVICE_HANDLE_PATTERN.test(value);
}

/** `featit-app@macbook-pro` as a folder, or `null`. */
export function parseFileReferenceFolder(value: string): IFileReferenceFolder | null {
  const at = value.lastIndexOf('@');
  if (at <= 0) return null;
  const name = value.slice(0, at);
  const device = value.slice(at + 1);
  if (!new RegExp(`^${FOLDER_NAME}$`).test(name) || name === '.' || name === '..') return null;
  if (!isDeviceHandle(device)) return null;
  return { name, device };
}
